
import React from "react";
import { Schemes, Utils } from "../../../../../utils";
import { i18nManager } from "../../../../../i18n";
import { Elements } from "../../../../shared/Elements";
import { ConfirmDialog, Siren } from "../../../../shared/ConfirmDialog";
import * as ReactRouterDom from "react-router-dom";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import { ApiResponseData, BaseService, ConsusService, HostService, IHost, IHostBlogArticle, IHostBlogCategory, IHostWebsite, IMember, IProfileWebsite, ITeam, IUploadedFile, MemberService, SanitizedResponse } from "../../../../../services";
import { Button, ButtonManageRef, Checkbox, ComposedPassword, DateTimeInput, DateTimePicker, DOMHelper, Dropdown, Form, FormControl, NoseurObject, ObjectHelper, Scheme, TabPane, TabPanel, TextAreaInput, TextInput, useScreenDimension } from "@rivtn/noseur";
import ImportExport from "../../../../page_design/ImportExport";
import MediaInput, { MediaInputManageRef } from "../../../../shared/MediaInput";
import { ArticleDialog } from "../../../../shared/settings/ArticleDialog";

export interface ArticlesProps {
    host: IHost;
    profile: IMember;
    hostWebsite: IHostWebsite;
}

function Articles(props: ArticlesProps) {

    const { host } = props;
    const { width } = useScreenDimension();
    const hostService = HostService.getInstance();
    const consusService = ConsusService.getInstance();
    const [profile, _] = React.useState<IMember>(props.profile);
    const labels = i18nManager.Labels.host.settings.blog.articles;
    const mediaInputManageRef = React.useRef<MediaInputManageRef>(null);
    const [hostWebsite, setHostWebsite] = React.useState<IHostWebsite>(props.hostWebsite);
    const [hostBlogCategories, setHostBlogCategories] = React.useState<IHostBlogCategory[]>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [hostBlogArticles, setHostBlogArticles] = React.useState<ApiResponseData<IHostBlogArticle>>({});

    React.useEffect(() => {
        fetchHostBlogArticles();
        fetchHostBlogCategories();
    }, []);

    return (<Form scheme={Schemes.OMNEHOST} className="profile-settings team-settings" style={{ padding: 0, boxShadow: "none" }}>
        <Checkbox label={labels.enable_ads} defaultChecked={hostWebsite.blog_settings.enable_rivtn_ad_on_articles_page} onChecked={(checked) => {
            hostService.updateHostWebsite(host.external_id, { ...hostWebsite, blog_settings: { ...hostWebsite.blog_settings, enable_rivtn_ad_on_articles_page: checked } }).then(({ }: SanitizedResponse<IHostWebsite>) => {
                Siren.alert(labels.update_successful);
            }).catch(BaseService.reportError);
        }} />
        <hr style={{ marginBottom: 0 }} />
        <div className="pagination">
            {Elements.buildPaginator(hostBlogArticles ?? {},
                ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams, fetchHostBlogArticles),
                parseInt(searchParams.get("page") ?? "1"), { margin: 15, marginBottom: 5 })}
            {Elements.buildSorter(labels.sorts, (sort) => Utils.updateSearchParamses({ sort }, setSearchParams, fetchHostBlogArticles), labels.sorts[0].label)}
        </div>
        <div style={{ display: "flex", marginTop: 10, justifyContent: "space-between", flexDirection: (width > 800 ? "row" : "column") }}>
            <Form scheme={Schemes.OMNEHOST} style={{ display: "flex", flexDirection: (width > 800 ? "row" : "column") }}>
                <Dropdown noIcon scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.category, style: { width: (width > 800 ? 150 : "100%") } }}
                    selectedOptionIndex={(hostBlogCategories?.findIndex((p) => p?.external_id === searchParams.get("cat")))}
                    textInputProps={{ style: { paddingLeft: 15 } }}
                    options={hostBlogCategories} optionMap={{
                        label: "name",
                        value: "external_id",
                    }} onSelectOption={(option) => {
                        Utils.updateSearchParams("cat", option.value, setSearchParams, fetchHostBlogArticles);
                    }} />
                {(width > 800) && (<>
                    <FormControl isFieldset label={labels.title} style={{ marginLeft: 15, width: 200 }}>
                        <TextInput defaultValue={searchParams.get("title") ?? ""} onInputComplete={(v) => {
                            Utils.updateSearchParams("title", v, setSearchParams, fetchHostBlogArticles);
                        }} />
                    </FormControl>
                    <FormControl isFieldset label={labels.content} style={{ marginLeft: 15, width: 200 }}>
                        <TextInput defaultValue={searchParams.get("content") ?? ""} onInputComplete={(v) => {
                            Utils.updateSearchParams("content", v, setSearchParams, fetchHostBlogArticles);
                        }} />
                    </FormControl>
                </>)}
            </Form>
            <Button raised scheme={Schemes.OMNEHOST} text={labels.create_article} style={{ marginTop: (width > 800 ? 10 : 15), padding: "10px 35px" }} onClick={() => addOrUpdate()} />
        </div>
        <hr style={{ margin: "20px 0px" }} />
        {!hostBlogArticles.content ? Elements.LOADING_STATE : (<div className="ho-arts">
            {hostBlogArticles.content?.map((hostBlogArticle) => {
                return (<div key={hostBlogArticle.external_id} className="ho-art" onClick={() => addOrUpdate(hostBlogArticle)}>
                    <span className="t lined-limit">{hostBlogArticle.title}</span>
                    <div className="c">
                        {(!!hostBlogArticle.extra_data?.featured_image) && (<img alt={hostBlogArticle.title} src={hostBlogArticle.extra_data?.featured_image} />)}
                        <span className="lined-limit">{hostBlogArticle.content}</span>
                    </div>
                </div>);
            })}
        </div>)}
        {hostBlogArticles.content && !hostBlogArticles.content.length && (Elements.buildEmptyListView("fa fa-circle", (<span>
            {labels.empty_label_parts.no_art}{" "}
            <span className="link" onClick={() => addOrUpdate()}>{labels.empty_label_parts.create}</span>{" "}
            {labels.empty_label_parts.a_new_art}
        </span>)))}
    </Form>);

    function fetchHostBlogCategories() {
        setHostBlogCategories(undefined);
        hostService.queryHostBlogCategories(host.external_id, {
            name: searchParams.get("name"),
            sort: searchParams.get("sort") ?? "",
            description: searchParams.get("desc"),
            page: searchParams.get("page") ?? "1",
            size: searchParams.get("size") ?? "100",
        }).then(({ content }: SanitizedResponse<IHostBlogCategory>) => {
            setHostBlogCategories(content);
        }).catch(BaseService.reportError);
    }

    function fetchHostBlogArticles() {
        setHostBlogArticles({});
        hostService.queryHostBlogArticles(host.external_id, {
            title: searchParams.get("title"),
            sort: searchParams.get("sort") ?? "",
            content: searchParams.get("content"),
            page: searchParams.get("page") ?? "1",
            size: searchParams.get("size") ?? "100",
        }).then(({ apiResponse }: SanitizedResponse<IHostBlogArticle>) => {
            setHostBlogArticles(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function addOrUpdate(article?: IHostBlogArticle) {
        const dialogLabels = labels.dialog.create_edit;
        let selectedCategory: IHostBlogCategory | undefined = hostBlogCategories?.find((c) => c.external_id === article?.category_id);

        const dialog = ArticleDialog({
            article,
            labels: {
                update: dialogLabels.update,
                delete: dialogLabels.delete,
                publish: dialogLabels.publish,
                title: dialogLabels.create_team_news,
                titlePlaceholder: dialogLabels.title,
                delete_desc: labels.dialog.delete.desc,
                delete_title: labels.dialog.delete.title,
                updateTitle: dialogLabels.update_or_delete,
                contentPlaceholder: dialogLabels.content_in_markdown,
            },
            preComponents: (<>
                <Dropdown fill noIcon scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: dialogLabels.search_and_select_a_cat, }}
                    selectedOptionIndex={hostBlogCategories?.findIndex((p) => p?.external_id === (article?.category_id ?? article?.host_blog_category?.external_id))}
                    textInputProps={{ style: { paddingLeft: 15 } }}
                    options={hostBlogCategories} optionMap={{
                        label: "name",
                        value: "external_id",
                    }} onSelectOption={(option) => {
                        selectedCategory = option;
                    }} />
            </>),
            secondaryComponents: (<>
                <Button textOnly scheme={Schemes.OMNEHOST} text={<span style={{ fontWeight: "bold", color: "var(--mainColor)" }}>{dialogLabels.view_comments}</span>} onClick={() => {
                    dialog.hide();
                }} />
            </>),
            onPublishOrUpdate: (isCreate: boolean, title: string, content: string, selectedFile?: File | null, done?: (error?: string) => void) => {
                const featuredImageDeleted = selectedFile === null && article?.extra_data?.featured_image;
                const method = (!isCreate ? hostService.updateHostBlogArticle : hostService.createHostBlogArticle).bind(hostService);
                method(host.external_id, { title, content, category_id: selectedCategory?.external_id, external_id: article?.external_id, featured_image: (featuredImageDeleted ? null : article?.extra_data?.featured_image) }).then(({ sanitized: articleSanitized }) => {
                    Siren.alert(isCreate ? dialogLabels.publish_successful : dialogLabels.update_successful);
                    if (selectedFile) {
                        consusService.uploadFile("/host/article/featured/", `featured_${articleSanitized?.external_id}`, selectedFile).then(async ({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                            await hostService.updateHostBlogArticle(host.external_id, { external_id: articleSanitized.external_id, featured_image: sanitized.consus_download_url });
                        }).catch(BaseService.reportError);
                    } else if (featuredImageDeleted) {
                        consusService.deleteFile([article?.extra_data?.featured_image]).catch(BaseService.reportError);
                    }
                    dialog.hide(() => {
                        done?.();
                        fetchHostBlogArticles();
                    });
                }).catch((error: any) => {
                    done?.(error.errorMessage);
                    BaseService.reportError(error);
                });
            },
            onDelete: (article: { external_id?: string; }, done?: (error?: string) => void) => {
                hostService.deleteHostBlogArticle(host.external_id, article!).then(() => {
                    Siren.alert(dialogLabels.delete_successful);
                    dialog.hide(() => {
                        done?.();
                        fetchHostBlogArticles();
                    });
                }).catch((error: any) => {
                    done?.(error.errorMessage);
                    BaseService.reportError(error);
                });
            }
        });
        dialog.show();
    }

}

export default Articles;
